import MainHeader from '@/components/MainHeader/MainHeader'
import MainHeaderOverridesProvider from '@/components/MainHeader/contexts/Overrides'
import { ColorEnum } from '@/constants/color'
import { ToastProvider } from '@/features/common'
import Error from '@/features/common/components/Error'
import NotificationCenter from '@/features/notifications/components/NotificationCenter'
import { useForegroundNotifications } from '@/features/notifications/hooks/useForegroundNotifications'
import { useNotification } from '@/features/notifications/hooks/useNotification'
import { usePushNotificationEvent } from '@/features/notifications/hooks/usePushNotificationEvent'
import { useLoadPrinters } from '@/features/print/hooks/useLoadPrinters'
import { useAppSelector } from '@/hooks/redux'
import { useScreenLayout, useScreenListener } from '@/hooks/screen'
import { useAuthLoaded } from '@/hooks/useAuthLoaded'
import { useHideSplashOnLoad } from '@/hooks/useHideSplashOnLoad'
import { useLockOrientationOnSmallScreen } from '@/hooks/useLockOrientationOnSmallScreen'
import useRegisterSentryNavigationIntegration from '@/hooks/useRegisterSentryNavigationIntegration'
import '@/logger'
import { authSelectors } from '@/redux/reducers/auth/selectors'
import { navigationIntegration } from '@/setup'
import * as Sentry from '@sentry/react-native'
import { Stack } from 'expo-router'
import { FC } from 'react'

const Layout: FC = () => {
    // fix header appearing on web before redirect when not authed
    const completed = useAppSelector(authSelectors.completed)

    const { loaded: authLoaded, error: authLoadError, retry: retryAuthLoad } = useAuthLoaded()

    const screenLayout = useScreenLayout()

    useScreenListener()
    useRegisterSentryNavigationIntegration(navigationIntegration)
    useLoadPrinters()
    useNotification()
    usePushNotificationEvent()
    useForegroundNotifications()
    useLockOrientationOnSmallScreen()
    useHideSplashOnLoad(authLoaded)

    if (!authLoaded) {
        // TODO: add loader component
        return null
    }

    if (authLoadError) {
        return (
            <Error
                title={authLoadError.title}
                message={authLoadError.message}
                retry={retryAuthLoad}
            />
        )
    }

    return (
        <ToastProvider>
            <NotificationCenter.PaperPortal>
                <MainHeaderOverridesProvider>
                    <Stack
                        screenOptions={{
                            headerShown: false,
                            contentStyle: { backgroundColor: ColorEnum.JET_800 }, // prevent white screen flickering when changing screen
                        }}
                    >
                        <Stack.Screen
                            name="(main)"
                            options={{
                                headerShown: completed && screenLayout === 'extended',
                                header: () => <MainHeader />,
                            }}
                        />
                    </Stack>
                </MainHeaderOverridesProvider>
            </NotificationCenter.PaperPortal>
        </ToastProvider>
    )
}

export default Sentry.wrap(Layout)
